<template>
  <div class="chart-top-container" :style="{ height: height + 'px' }">
    <!-- Add a ref to the chart container -->
    <div ref="echart" :style="{ height: height + 'px' }"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent
} from 'echarts/components';

// Use necessary components from ECharts
echarts.use([
  CanvasRenderer,
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent
]);

export default {
  props: {
    name: {type: String, default: 'Movement Analysis Graph'},
    chartData: {type: Object, required: true},
    height: {type: Number, required: true},
    page: {type: String, default: ''}
  },
  data() {
    return {
      chartInstance: null
    };
  },
  computed: {
    corporateConfig() {
      return this.$store.state.userInfo?.corporate?.config || {};
    },
    series() {
      let feedbacks = this.page === "progress" ? this.chartData.feedbackData : this.chartData.chartData.feedback;
      let stickers = Object.keys(feedbacks);
      let labels = this.page === "progress" ? this.chartData.labels : this.chartData.chartData.labels;

      const xAxis = stickers.map(sticker => {
        return {
          data: labels.map((label, index) => {
            return {
              value: parseInt(feedbacks[sticker][index]),
              name: label
            };
          }),
          name: sticker,
          type: 'bar',
          barMaxWidth: '40'
        };
      });

      const repsData = this.page === "progress" ? this.chartData.repsData : this.chartData.chartData.repsData;

      if (!this.corporateConfig.hideReps && repsData) {
        xAxis.push({
          data: labels.map((label, index) => {
            return {
              value: parseInt(repsData[index]),
              name: label
            };
          }),
          name: 'Number of Reps',
          type: 'line',
          color: '#e14040',
          lineStyle: {width: 4},
          smooth: false,
          symbol: "emptyTriangle",
          symbolSize: 12
        });
      }

      return xAxis;
    },
    chartOptions() {
      let groups = this.series.map(item => item.name);
      let labels = this.page === "progress" ? this.chartData.labels : this.chartData.chartData.labels;
      let feedbacks = this.page === "progress" ? this.chartData.feedbackData : this.chartData.chartData.feedback;

      // Extract unique dates and group sets under them
      const dateToSetsMap = labels.reduce((acc, label) => {
        const splits = label.split(' Set ');
        const datePart = splits[0];
        const setPart = label.slice(datePart.length).trim();

        acc[datePart] = acc[datePart] || [];
        acc[datePart].push(setPart);
        return acc;
      }, {});

      let dates = Object.keys(dateToSetsMap);
      let sets = dates.flatMap(date => dateToSetsMap[date]);

      // Compute boundaries for date labels
      const dateSpans = dates.reduce((acc, date, index) => {
          const start = acc.lastEnd || 0;
          const end = start + dateToSetsMap[date].length - 1;
          acc.bounds.push({ date, start, end });
          acc.lastEnd = end + 1;
          return acc;
      }, { bounds: [], lastEnd: 0 }).bounds;

      // Create the positions for the date axis based on the number of sets for each date
      const dateTicks = [];
      const dateTickLabels = [];
      dateSpans.forEach((span) => {
          let dateLabel;
          if (this.page === "progress") dateLabel = span.date.split('/').slice(0, 2).join('/'); // YYYY-MM-DD
          else dateLabel = span.date.split('-').slice(1, 3).reverse().join('/'); // DD/MM/YYYY
          for (let i = span.start; i <= span.end; i++) {
              dateTicks.push(i); // Position date ticks at the first set of each date group
              dateTickLabels.push(dateLabel); // Assign the same date label to all sets under the same date
          }
      });

      const stickersName = Object.keys(feedbacks);

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {type: 'shadow'},
          confine: true,
          formatter: (params) => {
              if (params.length === 0) return '';

              const iconForNumberOfReps = `<span style="display: inline-block; margin-right: 4px; width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-bottom: 10px solid #e14040;"></span>`;
              
              let tt = `
                <div style="margin: 10px 0 0; line-height: 1">
                  <div style="font-size: 14px; color: #666; font-weight: 400; line-height: 1">
                    ${params[0].axisValueLabel}
                  </div>`;

              params.forEach((param) => {
                tt += `
                  <div style="margin: 10px 0 0; line-height: 1">
                    ${param.seriesName !== "Number of Reps" ? param.marker : iconForNumberOfReps}
                    <span style="font-size: 14px; color: #666; font-weight: 400; margin-left: 2px;">
                      ${param.seriesName}
                    </span>
                    <span style="float: right; margin-left: 20px; font-size: 14px; color: #666; font-weight: 900;">
                      ${param.value ?? 'N/A'}
                    </span>
                  </div>
                  <div style="clear: both">
                  `;
              });

              tt += `<div style="clear: both"></div></div>`;

              return tt;
            }

        },
        legend: {
          data: stickersName,
          bottom: 0
        },
        grid: {
          top: '10%',
          left: '3%',
          right: '4%',
          bottom: '20%',
          containLabel: true,
        },
        xAxis: [
          {
            type: 'category',
            data: sets,
            boundaryGap: true,
            axisLabel: {
              color: '#000000',
              rotate: 45, // Rotate labels for better readability
            },
            axisLine: {
              lineStyle: {
                // color: '#FFFFFF',
                type: 'dashed',
                dashOffset: 4,
                opacity: 0.5
              }
            },
            axisTick: {
              show: true,
              lineStyle: {
                color: '#000000',
                width: 2,
              }
            },
            position: 'bottom',
            offset: 30
          },
          {
            type: 'category',
            data: dateTickLabels, // Use dateTickLabels to align dates with sets
            boundaryGap: true,
            offset: 20,
            axisLabel: {
              color: '#000000',
              fontWeight: 'bold',
              align: 'center',
              formatter: (value, index) => {
                return value; // Return the formatted date (DD/MM)
              },
            },
            axisTick: {
              show: true,
              lineStyle: {
                color: '#000000',
                width: 2
              }
            },
            axisLine: {
              show: true,
              lineStyle: {
                  type: 'solid',
              },
            },
          }
        ],
        yAxis: {
          type: 'value',
          name: this.name,
          nameLocation: 'middle',
          nameGap: 50
        },
        dataZoom: [
          {
            type: 'slider', // Slider for user interaction
            xAxisIndex: [0,1], // Apply to the sets axis
            start: 100,
            end: 25,
            top: '2%', // Position above the top axis
          },
          {
            type: 'inside', // Scrollwheel zoom for smooth experience
            xAxisIndex: [0,1], // Apply to the sets axis
            start: 100,
            end: 25,
          },
        ],
        series: this.series
      };
    }
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart);
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },
  methods: {
    initChart() {
      // Initialize the ECharts instance
      this.chartInstance = echarts.init(this.$refs.echart);
      // Set the chart options
      this.chartInstance.setOption(this.chartOptions);
    },
    resizeChart() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    }
  },
  watch: {
    chartOptions: {
      handler(newOptions) {
        if (this.chartInstance) {
          this.chartInstance.setOption(newOptions);
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.chart-top-container {
  width: 100%;
}
</style>
